// components/ui/button.tsx
import { type ButtonHTMLAttributes, forwardRef } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'destructive' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className = '', variant = 'default', size = 'default', ...props },
    ref,
  ) => {
    // Base styles
    let variantClasses = '';
    let sizeClasses = '';

    // Variant styles
    switch (variant) {
      case 'default':
        variantClasses =
          'bg-primaryColor text-white hover:bg-primaryColor/90 transition-all duration-200 active:scale-95';
        break;
      case 'outline':
        variantClasses =
          'bg-transparent border border-gray-300 text-primaryColor hover:bg-secondaryColor focus:ring-gray-500';
        break;
      case 'destructive':
        variantClasses =
          'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
        break;
      case 'ghost':
        variantClasses =
          'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500';
        break;
      case 'link':
        variantClasses =
          'bg-transparent text-blue-600 hover:underline focus:ring-blue-500 p-0';
        break;
    }

    // Size styles with responsive adjustments
    switch (size) {
      case 'default':
        sizeClasses = 'h-9 sm:h-10 px-3 sm:px-4 py-2 text-sm sm:text-base';
        break;
      case 'sm':
        sizeClasses = 'h-8 px-2 sm:px-3 py-1 text-xs sm:text-sm';
        break;
      case 'lg':
        sizeClasses = 'h-11 sm:h-12 px-4 sm:px-6 py-3 text-base sm:text-lg';
        break;
      case 'icon':
        sizeClasses = 'h-9 w-9 sm:h-10 sm:w-10 p-2';
        break;
    }

    return (
      <button
        className={`inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ${variantClasses} ${sizeClasses} ${className}`}
        ref={ref}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';
